import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LISTING_TYPES } from "@/lib/api";
import { toast } from "sonner";

export default function SellMachine() {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ name: "", mobile: "", whatsapp: "", listing_type: "used", machine_name: "", category: "", city: "", state: "", price: "", message: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get("/categories", { params: { all: true } }).then((r) => setCategories(r.data)).catch(() => {}); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim() || !form.machine_name.trim()) {
      toast.error("Name, mobile and machine name are required");
      return;
    }
    setLoading(true);
    try {
      await api.post("/sell-leads", form);
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Seo title="List Your Machine or Spare Part | Mashiniya" description="Have a new machine, used machine or spare part to sell? Share your product details with Mashiniya and our team will contact you. Buyer and seller enquiries are free." canonical={window.location.origin + "/sell-your-machine"} />
      <div className="bg-slate-900 py-14 text-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h1 className="font-display text-3xl font-extrabold sm:text-4xl">HAVE A MACHINE OR SPARE PART TO LIST?</h1>
          <p className="mt-3 text-lg text-slate-300">Share your product details with us and our team will contact you.</p>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
        {done ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center" data-testid="sell-success">
            <CheckCircle2 className="mx-auto mb-4 h-16 w-16 text-emerald-500" />
            <h2 className="font-display text-2xl font-extrabold text-slate-900">Thank you!</h2>
            <p className="mt-2 text-slate-600">Your machine details have been received. Our team will contact you shortly.</p>
            <button onClick={() => setDone(false)} className="mt-6 rounded-full border border-slate-300 px-6 py-2.5 text-sm font-bold text-slate-700">Submit another</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
            <input tabIndex={-1} autoComplete="off" value={form.company} onChange={set("company")} name="company" className="hidden" />
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Listing Type</Label>
                <Select value={form.listing_type} onValueChange={(v) => setForm({ ...form, listing_type: v })}>
                  <SelectTrigger data-testid="sell-listing-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{LISTING_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Product Name *</Label><Input data-testid="sell-machine-name" value={form.machine_name} onChange={set("machine_name")} required /></div>
              <div><Label>Name *</Label><Input data-testid="sell-name" value={form.name} onChange={set("name")} required /></div>
              <div><Label>Mobile *</Label><Input data-testid="sell-mobile" value={form.mobile} onChange={set("mobile")} required /></div>
              <div><Label>WhatsApp</Label><Input data-testid="sell-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} /></div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="sell-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Approximate Price</Label><Input data-testid="sell-price" value={form.price} onChange={set("price")} /></div>
              <div><Label>City</Label><Input data-testid="sell-city" value={form.city} onChange={set("city")} /></div>
              <div><Label>State</Label><Input data-testid="sell-state" value={form.state} onChange={set("state")} /></div>
            </div>
            <div><Label>Message</Label><Textarea data-testid="sell-message" value={form.message} onChange={set("message")} rows={4} /></div>
            <button data-testid="sell-submit" type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-full bg-brand py-3 font-bold text-white disabled:opacity-60">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} SUBMIT PRODUCT DETAILS
            </button>
            <p className="text-center text-xs text-slate-400">This is only a lead form. It does not create a public listing. Our team will contact you.</p>
          </form>
        )}
      </div>
    </Layout>
  );
}
